// @flow

import * as React from 'react';

import get from 'lodash/get';
import { useDispatch } from 'react-redux';
import { Outlet, useNavigate } from 'react-router-dom';
import { compose } from 'redux';
import { disbatch } from 'redux-act';

import { withPageMeta } from '@gandi/app-runner';
import { selectIsAuthenticatedUser, needsAuth } from '@gandi/authentication';
import { selectPricingInfos } from '@gandi/billing';
import {
  selectCartIsDeleted,
  selectCartIsLoaded,
  selectCartIsNotCreated,
  selectCountItems,
  selectCountTotalProducts,
  setCartUUID,
  getCart,
  resetCart,
  selectCartHasOrga,
  selectCartOwner,
  selectCartTotal,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { instances } from '@gandi/redux-resource-price';
import { selectCurrentId } from '@gandi/redux-sharing';
import { sendCommerce } from '@gandi/tracking';
import { useTranslator } from '@gandi/translate';

import { withLogger, type GandiLogger } from 'gandi.redux-logger';

import { withRouter } from 'src/helpers/withRouter';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { Intro } from '../Layout/components/Intro';

import { Layout } from '../components/Layout';
import {
  setPrefetchStatus,
  setAppWorking,
  unsetAppWorking,
  selectIsComingFromPrefetch,
  initializeOverview,
} from '../modules/app';
import { postCartCheckout } from '../modules/app/actions';
import { selectHasMissingOrgInfo } from '../modules/organization/selectors';
import type { FullState, PreFetchArgs, WithRouterProps } from '../types';

import { selectNeedDomainConfiguration } from './modules/selectors';

type Params = {
  cartId: string,
  orderId?: string,
};

type InjectedProps = {
  ...WithRouterProps<Params>,
  logger: GandiLogger,
};

type Props = {
  ...InjectedProps,
};

function OverviewRoot({ logger, params }: Props) {
  const dispatch = useDispatch();
  const { __ } = useTranslator();
  const navigate = useNavigate();

  const isAuthenticated = useAppSelector(selectIsAuthenticatedUser);
  const isCartLoaded = useAppSelector(selectCartIsLoaded);
  const isCartDeleted = useAppSelector(selectCartIsDeleted);
  const isCartNotCreated = useAppSelector(selectCartIsNotCreated);
  const totalProducts = useAppSelector(selectCountTotalProducts);
  const distinctProducts = useAppSelector(selectCountItems);
  const isComingFromPrefetch = useAppSelector(selectIsComingFromPrefetch);
  const cartHasOrga = useAppSelector(selectCartHasOrga);
  const cartOwner = useAppSelector(selectCartOwner);
  const currentId = useAppSelector(selectCurrentId);

  const hasMissingOrgInfo = useAppSelector(state =>
    selectHasMissingOrgInfo(state),
  );
  const needDomainConfiguration = useAppSelector(selectNeedDomainConfiguration);

  const currency = useAppSelector(selectPricingInfos).currency;
  const cartTotal = useAppSelector(selectCartTotal);

  const countItems = useAppSelector(selectCountItems);

  const hasProducts = totalProducts > 0;
  const isCartEmptyOrGone = isCartNotCreated || isCartDeleted || !hasProducts;
  const preFetchFailed = !isCartLoaded && !isCartNotCreated && !isCartDeleted;
  const cartMisconfigured =
    isAuthenticated &&
    (!cartHasOrga ||
      cartOwner !== currentId ||
      hasMissingOrgInfo ||
      needDomainConfiguration);

  // we need to make sure this component will not render before the UI is flagged
  const mounted = React.useRef(false);
  React.useEffect(() => {
    if (!mounted.current) {
      if (global.__CLIENT__ && !isComingFromPrefetch) {
        // Add global spinner while the overview is initializing
        dispatch(setAppWorking());
      }
      mounted.current = true;
    }
  }, [dispatch, isComingFromPrefetch]);

  const hasInitialized = React.useRef(false);
  React.useEffect(() => {
    if (hasInitialized.current) return;

    if (preFetchFailed) {
      // preFetch failed (e.g.: timeout); let's refetch the cart once client-side.
      logger.warn('Overview not initialized, trying one more time.');
      hasInitialized.current = true;

      Promise.resolve()
        .then(() => dispatch(setCartUUID(params.cartId)))
        .then(() => dispatch(initializeOverview({ orderId: params.orderId })))
        .finally(() => dispatch(unsetAppWorking()));
      return;
    }

    if (isCartEmptyOrGone) {
      disbatch(dispatch, resetCart(), unsetAppWorking());
      return;
    }

    if (cartMisconfigured) {
      navigate(`/cart/${params.cartId}/configure/account-choice`, {
        replace: true,
      });
      disbatch(dispatch, setPrefetchStatus(false), unsetAppWorking());
      return;
    }

    if (hasInitialized.current) return;
    hasInitialized.current = true;

    Promise.resolve()
      .then(() => dispatch(setCartUUID(params.cartId)))
      /**
       * Hack to avoid UI blinks without having complex selectors to know
       * if componentDidMount have to dispatch some actions...
       * FIXME: Implement "complex" selectors and remove this error-prone hack.
       */
      .then(() => {
        if (isComingFromPrefetch) {
          dispatch(setPrefetchStatus(false));

          dispatch(unsetAppWorking());

          // Recover: initializeOverview ran server-side, so we execute the dry-runs now on the client
          return dispatch(postCartCheckout());
        }

        return dispatch(initializeOverview({ orderId: params.orderId }));
      })
      .catch(err => {
        // Overview initialization was stopped because Cart Owner needs to be specified by user.
        if (err.name === 'CartError') {
          navigate(`/cart/${params.cartId}/configure/account-choice`, {
            replace: true,
          });
          disbatch(dispatch, setPrefetchStatus(false), unsetAppWorking());
          return;
        }

        // No need to display an error notification if user just went back to a deleted cart.
        // Just clear the current cart state and be done with it.
        if (err.status === 'deleted') {
          dispatch(resetCart());
          return;
        }

        logger.error({ err }, 'Error when initializing the overview.');

        dispatch(
          notifyDanger(
            get(
              err,
              '_error',
              __('Unable to initialize the Cart. Please try again later.'),
            ),
          ),
        );
      })
      // Remove the Global Spinner
      .finally(() => dispatch(unsetAppWorking()));
  }, [
    __,
    cartMisconfigured,
    dispatch,
    isCartEmptyOrGone,
    isComingFromPrefetch,
    logger,
    navigate,
    params.cartId,
    params.orderId,
    preFetchFailed,
  ]);

  const hasTracked = React.useRef(false);
  React.useEffect(() => {
    // Only register a hit if the user didn't get redirected
    if (
      !preFetchFailed &&
      !isCartEmptyOrGone &&
      !cartMisconfigured &&
      !hasTracked.current
    ) {
      hasTracked.current = true;

      sendCommerce({
        name: 'cart.display',
        data: {
          cart_id: params.cartId,
          cart_currency: currency,
          cart_turnovertaxincluded: cartTotal.afterTaxes,
          cart_turnovertaxfree: cartTotal.beforeTaxes,
          cart_quantity: totalProducts,
          cart_nbdistinctproduct: distinctProducts,
        },
      });
    }
  }, [
    cartMisconfigured,
    cartTotal.afterTaxes,
    cartTotal.beforeTaxes,
    currency,
    distinctProducts,
    isCartEmptyOrGone,
    params.cartId,
    preFetchFailed,
    totalProducts,
  ]);

  const willReceiveProps = React.useRef(true);
  React.useEffect(() => {
    if (willReceiveProps.current) {
      willReceiveProps.current = false;
      return;
    }
    if (isCartEmptyOrGone) {
      dispatch(instances.invalidate());
    }
  }, [dispatch, isCartEmptyOrGone]);

  // Show a blank page while redirecting to account-choice.
  if (cartMisconfigured && hasProducts) {
    return null;
  }

  // For EmptyPage
  const backgroundColor =
    countItems === 0 || isCartDeleted || isCartNotCreated
      ? 'surface'
      : undefined;

  return (
    <Layout
      backgroundColor={backgroundColor}
      lockedOrga
      showCartSummary={hasProducts}
      currentStep={2}
    >
      {!isAuthenticated && hasProducts && (
        <Intro title={__("We're almost there! Let's recap your products.")} />
      )}

      <Outlet />
    </Layout>
  );
}

/**
 *  README:
 *  Mitigation/temporary solution to catch users attempting to view a
 *  non-anonymous cart, for which invoicing orga has been chosen,
 *  with a different sharing_id than the owner's.
 *  Redirect these users to the owner's sharing_id.
 *  Let others pass through.
 */
OverviewRoot.handleRedirect = async (
  args: PreFetchArgs<Props, Params, void, FullState>,
) => {
  const { dispatch, getState, req, res } = args;
  const current = selectCurrentId(getState());

  const authenticated = !!current;
  if (authenticated) {
    await dispatch(getCart(args.params.cartId));
    const hasOrga = selectCartHasOrga(getState());
    const owner = selectCartOwner(getState());

    if (!hasOrga) {
      const redirectTo = req.originalUrl.replace(
        'overview',
        'configure/account-choice',
      );
      res.redirect(redirectTo);
      throw new Error('Redirecting to account choice');
    }

    if (owner && owner !== current && hasOrga) {
      const redirectTo = req.originalUrl.replace(current, owner);
      res.redirect(redirectTo);
      throw new Error('Redirecting to cart owner context');
    }
  }
};

OverviewRoot.preFetch = (args: PreFetchArgs<Props, Params>) => {
  const {
    logger,
    dispatch,
    params: { cartId, orderId },
  } = args;

  return Promise.resolve()
    .then(() => dispatch(setCartUUID(cartId)))
    .then(() => dispatch(initializeOverview({ orderId })))
    .then(() => dispatch(setPrefetchStatus(true)))
    .catch(err => {
      logger.error({ err }, 'Error when initializing the overview.');
    });
};

export default (compose(
  withLogger('shop/overview'),
  withRouter,
  withPageMeta(args => ({
    title: args.__('Your shopping cart - Gandi.net'),
    description: args.__(
      'Review your shopping cart, add a promo code, review contracts, continue shopping.',
    ),
  })),
  needsAuth,
)(OverviewRoot): React.ComponentType<{ ... }> & {
  preFetch: mixed => null | Promise<mixed>,
});
